import React, {useCallback, useState} from 'react'
import {useAccountContext} from '../layout/AccountLayout'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useAppDispatch} from '../../store/hooks'
import {getNavigationPaths} from '../../services/navigation'
import {
  residentPortalApi,
  useGetPaymentAccountsQuery,
  useGetPaymentMethodsQuery,
  useGetPaymentSessionQuery,
  useGetPropertyQuery,
  useMakePaymentMutation,
  useGetPaymentMaxQuery
} from '../../services/residentPortalApi'
import Loader from '../common/Loader'
import AdyenPaymentForm from '../common/AdyenPaymentForm'
import AlertMessage from '../common/AlertMessage'
import SubmitButton from '../common/SubmitButton'
import PaymentAmountInput from './PaymentAmountInput'
import PaymentMethodSelect from './PaymentMethodSelect'
import Breadcrumbs from '../navigation/Breadcrumbs'
import PaymentTabs from './PaymentTabs'
import toast from 'react-hot-toast'
import {getLogger} from '../../services/logging'
import TermsAndConditions from '../common/TermsAndConditions'
import Alert from '../common/Alert'
import {PaymentOrigin, PaymentType} from '../../types/app-types'
import {
  debounce,
  filterAllowedPaymentMethods,
  formatAmount
} from '../../services/utils'
import currency from 'currency.js'
import {UseQueryHookResult} from '@reduxjs/toolkit/dist/query/react/buildHooks'
import PaymentMethodsResponse from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse'

enum PaymentFormView {
  DISABLED = 'disabled',
  ACCOUNT_LOADING = 'account_loading',
  ACCOUNT_READY = 'account_ready',
  ONE_TIME_LOADING = 'one_time_loading',
  ONE_TIME_ERROR = 'one_time_error',
  ONE_TIME_READY = 'one_time_ready'
}

interface MakePaymentState {
  paymentType: PaymentType
  amount: number
  paymentAccount?: any
}

const logger = getLogger('MakePaymentPage')

/**
 * Determines the payment form view to display
 * @param paymentAccount
 * @param paymentType
 * @param amount
 * @param paymentSessionQuery
 * @param paymentMethodsQuery
 */
function getPaymentFormView(
  {amount, paymentAccount, paymentType}: MakePaymentState,
  paymentSessionQuery: UseQueryHookResult<any, any>,
  paymentMethodsQuery: UseQueryHookResult<any, any>
): PaymentFormView {
  // disable form until payment amount and method are entered
  if (!amount || amount <= 0 || !paymentType) {
    return PaymentFormView.DISABLED
  }

  if (isPaymentAccount(paymentType)) {
    if (isPaymentAccount(paymentType) && paymentAccount) {
      return PaymentFormView.ACCOUNT_READY
    }

    // default
    return PaymentFormView.ACCOUNT_LOADING
  } else {
    if (paymentSessionQuery.isLoading || paymentMethodsQuery.isLoading) {
      return PaymentFormView.ONE_TIME_LOADING
    }

    if (paymentSessionQuery.isError || paymentMethodsQuery.isError) {
      return PaymentFormView.ONE_TIME_ERROR
    }

    if (
      paymentSessionQuery.data &&
      paymentMethodsQuery.data?.paymentMethods?.length
    ) {
      return PaymentFormView.ONE_TIME_READY
    }

    // default
    return PaymentFormView.ONE_TIME_LOADING
  }
}

function getPaymentTotal(paymentState: MakePaymentState, fee: number): string {
  const {
    amount,
    paymentAccount: {adyenPaymentMethod}
  } = paymentState
  return adyenPaymentMethod === 'Credit Card'
    ? currency(amount)
        .multiply(1 + fee)
        .format()
    : currency(amount).format()
}

function isPaymentAccount(paymentType: PaymentType) {
  return (
    paymentType === PaymentType.AccountCC ||
    paymentType === PaymentType.AccountACH
  )
}

function isCreditCard(paymentType: PaymentType) {
  return (
    paymentType === PaymentType.AccountCC ||
    paymentType === PaymentType.OneTimePaymentCC
  )
}

const MakePaymentPage = () => {
  const {accountId, reservation} = useAccountContext()
  const dispatch = useAppDispatch()
  const paths = getNavigationPaths(accountId)
  const navigate = useNavigate()

  // get payment amount from query string
  const [searchParams] = useSearchParams()
  const amountParam = searchParams.get('amount')

  // set payment form state
  const [payment, updatePayment] = useState({
    amount: amountParam ? parseFloat(amountParam) : 0,
    paymentAccount: null,
    paymentType: null
  })

  // get all payment accounts for the user
  const paymentAccountsQuery = useGetPaymentAccountsQuery(
    reservation.property.id
  )

  // determine the max payment amount
  const paymentMaxQuery = useGetPaymentMaxQuery({
    propertyId: reservation.property.id,
    reservationId: reservation.id
  })
  const maxPayment = !paymentMaxQuery.isLoading
    ? paymentMaxQuery?.data?.maxPayment || 0
    : 0
  const reservationBalance = Math.abs(reservation?.balance?.amount)
  const maxAmount = Math.min(maxPayment, reservationBalance)

  // get adyen supported payment methods
  const paymentMethodsQuery = useGetPaymentMethodsQuery(reservation.property.id)
  const allowedPaymentMethods = paymentMethodsQuery.data
    ? filterAllowedPaymentMethods(payment.paymentType, paymentMethodsQuery.data)
    : ({paymentMethods: []} as PaymentMethodsResponse)

  // create an adyen payment session
  const skipPaymentForm =
    !payment.amount ||
    payment.amount === 0 ||
    isPaymentAccount(payment.paymentType)

  const paymentSessionParams = {
    amountInCents: payment.amount * 100,
    reservationId: reservation.id,
    propertyId: reservation.property.id,
    paymentAccount: false,
    paymentOrigin: PaymentOrigin.ResidentPortal,
    paymentType: payment.paymentType,
    returnUrl: `${window.location.origin}/#${paths.Account}`
  }
  const paymentSessionQuery = useGetPaymentSessionQuery(paymentSessionParams, {
    skip: skipPaymentForm
  })

  // get property-specific settings (ex. credit card fee)
  const propertyQuery = useGetPropertyQuery(reservation.property.id)
  const property = propertyQuery.data

  // init payment mutation - used for payment accounts only
  const [makePayment, makePaymentResult] = useMakePaymentMutation()

  const creditCardTxFee =
    isCreditCard(payment.paymentType) &&
    payment.amount &&
    payment.amount > 0 &&
    property.creditCardFee
      ? payment.amount * property.creditCardFee
      : 0

  // get payment form view
  const paymentFormView = getPaymentFormView(
    payment,
    paymentSessionQuery,
    paymentMethodsQuery
  )

  // use debouncing to prevent payment session requests from being fired for every keystroke
  // see: https://dmitripavlutin.com/react-throttle-debounce/
  const handlePaymentAmountChange = useCallback(
    debounce((amount: number) => {
      updatePayment({...payment, amount})
    }, 1000),
    [payment]
  )

  return (
    <section>
      <Breadcrumbs
        items={[
          {label: 'Account', path: paths.Account, active: false},
          {label: 'Make a Payment', active: true}
        ]}
      />

      <PaymentTabs accountId={accountId} />

      <div className="card-default w-full">
        <div className="card-body-default">
          <h1 className="card-header">Make a Payment</h1>

          <Alert type="info" className="mb-2">
            <ul className="list-disc">
              <li>
                Please enter a payment amount greater than{' '}
                <span className="font-bold">0</span> and choose a payment
                method.
              </li>
              {!propertyQuery.isLoading ? (
                <li>
                  Please note: an additional{' '}
                  <span className="font-bold">
                    {property.creditCardFee * 100}%
                  </span>{' '}
                  transaction fee will be charged for credit or debit card
                  payments.
                </li>
              ) : (
                ''
              )}
              {!paymentMaxQuery.isLoading ? (
                <li>
                  Payment Limit:{' '}
                  <span className="font-bold">{formatAmount(maxAmount)}</span>
                </li>
              ) : (
                ''
              )}
            </ul>
          </Alert>

          {/* Payment amount */}
          <PaymentAmountInput
            amount={payment.amount}
            maxAmount={maxAmount + 0.01}
            disable={paymentMaxQuery.isLoading}
            onPaymentAmountChange={handlePaymentAmountChange}
          />

          {/* Payment method select */}
          <PaymentMethodSelect
            className="mt-2 mb-4"
            disabled={
              paymentAccountsQuery.isLoading ||
              !payment.amount ||
              payment.amount <= 0
            }
            paymentAccounts={paymentAccountsQuery.data}
            onPaymentMethodSelect={(paymentType, paymentAccount) =>
              updatePayment({...payment, paymentType, paymentAccount})
            }
          />

          {/* Credit/debit card transaction fee message */}
          {isCreditCard(payment.paymentType) &&
          payment.amount &&
          payment.amount > 0 ? (
            <p className="text-red-500">
              A{' '}
              <span className="font-bold">{property.creditCardFee * 100}%</span>{' '}
              credit/debit card transaction fee of{' '}
              <span className="font-bold">{formatAmount(creditCardTxFee)}</span>{' '}
              will be applied.
            </p>
          ) : (
            ''
          )}

          {/* Account - Loading View */}
          {paymentFormView === PaymentFormView.ACCOUNT_LOADING ? (
            <Loader text="Loading payment account..." />
          ) : (
            ''
          )}

          {/* Account - Form View */}
          {paymentFormView === PaymentFormView.ACCOUNT_READY ? (
            <SubmitButton
              text={`Make Payment - ${getPaymentTotal(
                payment,
                property.creditCardFee
              )}`}
              disabled={payment.amount <= 0}
              loadText="Processing Payment..."
              loading={makePaymentResult.isLoading}
              onSubmit={() => {
                const {paymentAccount, amount} = payment
                logger.logInfo('Making payment', {
                  paymentAccountRef: paymentAccount.id,
                  reservationId: reservation.id,
                  amount
                })

                const mutationPromise = makePayment({
                  accountReference: paymentAccount.id,
                  amountInCents: amount * 100,
                  propertyId: reservation.property?.id,
                  reservationId: reservation.id
                }).unwrap()

                toast.promise(mutationPromise, {
                  loading: 'Payment submitting...',
                  success: 'Payment submitted',
                  error: 'Payment failed'
                })

                mutationPromise
                  .then(() => {
                    // invalidate cache
                    dispatch(residentPortalApi.util.invalidateTags(['Folio']))
                    navigate(paths.MakePaymentResult)
                  })
                  .catch((e: any) => {
                    logger.logError('Failed to make a payment.', {
                      error: e
                    })
                    navigate(`${paths.MakePaymentResult}?error=true`)
                  })
              }}
            />
          ) : (
            ''
          )}

          {/* One-Time Payment - Loading View */}
          {paymentFormView === PaymentFormView.ONE_TIME_LOADING ? (
            <Loader text="Loading payment information..." />
          ) : (
            ''
          )}

          {/* One-Time Payment - Error View */}
          {paymentFormView === PaymentFormView.ONE_TIME_ERROR ? (
            <AlertMessage
              type="error"
              message={
                'Unable to make a payment at the moment.' +
                'If the problem persists, please contact support: hello@placemakr.com'
              }
            />
          ) : (
            ''
          )}

          {/* One-Time Payment - Form View */}
          {paymentFormView === PaymentFormView.ONE_TIME_READY ? (
            <AdyenPaymentForm
              paymentMethods={allowedPaymentMethods}
              paymentSession={paymentSessionQuery.data}
              isSavedPayment={false}
              onPaymentCompleted={() => {
                toast.success('Payment submitted')
                dispatch(residentPortalApi.util.invalidateTags(['Folio']))
                navigate(`${paths.MakePaymentResult}`)
              }}
            />
          ) : (
            ''
          )}

          <div className="mt-4 border border-solid p-4 md:p-8">
            <h2 className="text-lg mb-4">Terms and Conditions</h2>
            <TermsAndConditions />
          </div>
        </div>
      </div>
    </section>
  )
}

export default MakePaymentPage
